/**
 * Security Admin View
 *
 * Centralized security dashboard for managing toggles, allowlists,
 * and hardening features. Reads/writes via the config form system.
 */

import { html, nothing } from "lit";

// ---------------------------------------------------------------------------
// Hardcoded constants (mirrored from backend -- read-only display)
// ---------------------------------------------------------------------------

const BLOCKED_OVERRIDE_PREFIXES = [
  "security",
  "gateway.auth",
  "gateway.password",
  "gateway.token",
  "gateway.tls",
  "hooks.gmail.allowUnsafeExternalContent",
  "hooks.internal.allowUnsafeExternalContent",
];

const ALLOWED_DOWNLOAD_DOMAINS = [
  "github.com",
  "raw.githubusercontent.com",
  "objects.githubusercontent.com",
  "registry.npmjs.org",
  "codeload.github.com",
  "releases.hashicorp.com",
];

const SSRF_BLOCKED = [
  "localhost",
  "metadata.google.internal",
  "*.localhost",
  "*.local",
  "*.internal",
];

const SUSPICIOUS_PATTERN_COUNT = 12;
const MAX_EVAL_FN_LENGTH = 100_000;
const SKILL_SCANNER_MAX_FILES = 500;
const SKILL_SCANNER_MAX_FILE_BYTES = 1_048_576;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SecurityAdminProps = {
  configForm: Record<string, unknown> | null;
  configLoading: boolean;
  configSaving: boolean;
  configDirty: boolean;
  connected: boolean;
  onConfigPatch: (path: Array<string | number>, value: unknown) => void;
  onConfigRemove: (path: Array<string | number>) => void;
  onConfigSave: () => void;
  onConfigReload: () => void;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getNestedValue(obj: Record<string, unknown> | null, path: string[]): unknown {
  if (!obj) return undefined;
  let current: unknown = obj;
  for (const key of path) {
    if (current == null || typeof current !== "object") return undefined;
    current = (current as Record<string, unknown>)[key];
  }
  return current;
}

function asBool(value: unknown, fallback = false): boolean {
  if (typeof value === "boolean") return value;
  if (value === "true") return true;
  if (value === "false") return false;
  return fallback;
}

function asStringArray(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(String);
  return [];
}

function asString(value: unknown, fallback = ""): string {
  if (typeof value === "string") return value;
  return fallback;
}

// ---------------------------------------------------------------------------
// Shared styles
// ---------------------------------------------------------------------------

const SECTION_STYLE =
  "border:1px solid var(--border);border-radius:8px;padding:16px;margin-bottom:16px;background:var(--bg-card);";
const SECTION_TITLE_STYLE = "font-size:1.05em;font-weight:600;margin-bottom:12px;";
const ROW_STYLE =
  "display:flex;align-items:center;justify-content:space-between;padding:8px 0;border-bottom:1px solid var(--border-subtle,rgba(128,128,128,0.15));";
const LABEL_STYLE = "font-size:0.9em;";
const HELP_STYLE = "font-size:0.78em;color:var(--fg-muted,#888);margin-top:2px;";
const DANGER_LABEL_STYLE = "font-size:0.9em;color:var(--danger,#e55);font-weight:500;";
const TAG_STYLE =
  "display:inline-flex;align-items:center;gap:4px;padding:3px 8px;border:1px solid var(--border);border-radius:4px;font-size:0.82em;background:var(--bg);margin:2px;";
const TAG_REMOVE_STYLE =
  "cursor:pointer;background:none;border:none;color:var(--fg-muted,#888);font-size:1em;padding:0 2px;line-height:1;";
const INFO_BOX_STYLE =
  "padding:10px 12px;border-radius:6px;background:var(--bg);border:1px solid var(--border-subtle,rgba(128,128,128,0.15));font-size:0.85em;";
const ADD_INPUT_STYLE =
  "padding:6px 8px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);font-size:0.85em;flex:1;min-width:140px;";

// ---------------------------------------------------------------------------
// Components
// ---------------------------------------------------------------------------

function renderToggle(
  label: string,
  help: string,
  checked: boolean,
  onChange: (next: boolean) => void,
  danger = false,
) {
  return html`
    <div style=${ROW_STYLE}>
      <div>
        <div style=${danger ? DANGER_LABEL_STYLE : LABEL_STYLE}>${label}</div>
        <div style=${HELP_STYLE}>${help}</div>
      </div>
      <label style="position:relative;display:inline-block;width:44px;height:24px;flex-shrink:0;margin-left:12px;">
        <input
          type="checkbox"
          .checked=${checked}
          @change=${(e: Event) => onChange((e.target as HTMLInputElement).checked)}
          style="opacity:0;width:0;height:0;position:absolute;"
        />
        <span style="
          position:absolute;cursor:pointer;inset:0;border-radius:12px;transition:background 0.2s;
          background:${checked ? (danger ? "var(--danger,#e55)" : "var(--accent,#4a9)") : "var(--border,#555)"};
        ">
          <span style="
            position:absolute;left:${checked ? "22px" : "2px"};top:2px;width:20px;height:20px;
            border-radius:50%;background:white;transition:left 0.2s;
          "></span>
        </span>
      </label>
    </div>
  `;
}

function renderTagList(
  items: string[],
  onRemove: ((item: string) => void) | null,
  onAdd: ((item: string) => void) | null,
  placeholder = "Add item...",
) {
  return html`
    <div style="display:flex;flex-wrap:wrap;gap:0;margin-top:6px;">
      ${items.map(
        (item) => html`
          <span style=${TAG_STYLE}>
            <span style="font-family:monospace;">${item}</span>
            ${
              onRemove
                ? html`<button
                  style=${TAG_REMOVE_STYLE}
                  @click=${() => onRemove(item)}
                  title="Remove"
                >x</button>`
                : nothing
            }
          </span>
        `,
      )}
    </div>
    ${
      onAdd
        ? html`
          <div style="display:flex;gap:6px;margin-top:8px;">
            <input
              type="text"
              placeholder=${placeholder}
              style=${ADD_INPUT_STYLE}
              @keydown=${(e: KeyboardEvent) => {
                if (e.key === "Enter") {
                  const input = e.target as HTMLInputElement;
                  const value = input.value.trim();
                  if (value && !items.includes(value)) {
                    onAdd(value);
                    input.value = "";
                  }
                }
              }}
            />
            <button
              class="btn btn--sm"
              @click=${(e: Event) => {
                const input = (e.target as HTMLElement).previousElementSibling as HTMLInputElement;
                const value = input?.value?.trim();
                if (value && !items.includes(value)) {
                  onAdd(value);
                  input.value = "";
                }
              }}
            >Add</button>
          </div>
        `
        : nothing
    }
  `;
}

function renderInfoBox(label: string, items: string[]) {
  return html`
    <div style="margin-bottom:12px;">
      <div style=${LABEL_STYLE}>${label}</div>
      <div style="${INFO_BOX_STYLE};margin-top:6px;">
        ${items.map(
          (item) =>
            html`<div style="padding:2px 0;font-family:monospace;font-size:0.9em;">${item}</div>`,
        )}
      </div>
    </div>
  `;
}

function renderStatusIndicator(label: string, ok: boolean, detail: string) {
  const color = ok ? "var(--accent,#4a9)" : "var(--warning,#e90)";
  return html`
    <div style="text-align:center;padding:12px 8px;">
      <div style="width:12px;height:12px;border-radius:50%;background:${color};margin:0 auto 6px;"></div>
      <div style="font-size:0.85em;font-weight:500;">${label}</div>
      <div style="font-size:0.75em;color:var(--fg-muted,#888);margin-top:2px;">${detail}</div>
    </div>
  `;
}

// ---------------------------------------------------------------------------
// Main render
// ---------------------------------------------------------------------------

export function renderSecurityAdmin(props: SecurityAdminProps) {
  const cfg = props.configForm;

  // Read config values
  const tlsEnabled = asBool(getNestedValue(cfg, ["gateway", "tls", "enabled"]));
  const tlsAutoGen = asBool(getNestedValue(cfg, ["gateway", "tls", "autoGenerate"]), true);
  const authMode = asString(getNestedValue(cfg, ["gateway", "auth", "mode"]), "token");
  const allowTailscale = asBool(getNestedValue(cfg, ["gateway", "auth", "allowTailscale"]));
  const allowInsecureAuth = asBool(
    getNestedValue(cfg, ["gateway", "controlUi", "allowInsecureAuth"]),
  );
  const disableDeviceAuth = asBool(
    getNestedValue(cfg, ["gateway", "controlUi", "dangerouslyDisableDeviceAuth"]),
  );
  const allowedOrigins = asStringArray(
    getNestedValue(cfg, ["gateway", "controlUi", "allowedOrigins"]),
  );
  const trustedProxies = asStringArray(getNestedValue(cfg, ["gateway", "trustedProxies"]));
  const allowCommands = asStringArray(getNestedValue(cfg, ["gateway", "nodes", "allowCommands"]));
  const denyCommands = asStringArray(getNestedValue(cfg, ["gateway", "nodes", "denyCommands"]));
  const tlsFingerprint = asString(getNestedValue(cfg, ["gateway", "remote", "tlsFingerprint"]));
  const gmailUnsafe = asBool(getNestedValue(cfg, ["hooks", "gmail", "allowUnsafeExternalContent"]));

  // Security health indicators
  const tlsOk = tlsEnabled;
  const authOk = !allowInsecureAuth && !disableDeviceAuth;
  const contentOk = !gmailUnsafe;

  return html`
    <div style="max-width:900px;">

      <!-- Security Health Overview -->
      <div style=${SECTION_STYLE}>
        <div style=${SECTION_TITLE_STYLE}>Security Health Overview</div>
        <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(100px,1fr));gap:4px;">
          ${renderStatusIndicator("TLS", tlsOk, tlsOk ? "Enabled" : "Disabled")}
          ${renderStatusIndicator("Auth", authOk, authOk ? "Secure" : "Weakened")}
          ${renderStatusIndicator("Content", contentOk, contentOk ? "Protected" : "Unsafe allowed")}
          ${renderStatusIndicator("SSRF", true, "Active")}
          ${renderStatusIndicator("Overrides", true, "Protected")}
          ${renderStatusIndicator("Scanner", true, `${SKILL_SCANNER_MAX_FILES} files`)}
        </div>
      </div>

      <!-- Gateway Authentication -->
      <div style=${SECTION_STYLE}>
        <div style=${SECTION_TITLE_STYLE}>Gateway Authentication</div>

        <div style=${ROW_STYLE}>
          <div>
            <div style=${LABEL_STYLE}>Auth Mode</div>
            <div style=${HELP_STYLE}>How clients authenticate with the gateway.</div>
          </div>
          <select
            .value=${authMode}
            @change=${(e: Event) =>
              props.onConfigPatch(
                ["gateway", "auth", "mode"],
                (e.target as HTMLSelectElement).value,
              )}
            style="padding:6px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);"
          >
            <option value="token" ?selected=${authMode === "token"}>Token</option>
            <option value="password" ?selected=${authMode === "password"}>Password</option>
          </select>
        </div>

        ${renderToggle(
          "Allow Tailscale Auth",
          "Accept auth from Tailscale network identity.",
          allowTailscale,
          (v) => props.onConfigPatch(["gateway", "auth", "allowTailscale"], v),
        )}

        ${renderToggle(
          "Allow Insecure Auth",
          "Allow unencrypted auth transport. DANGEROUS in production.",
          allowInsecureAuth,
          (v) => props.onConfigPatch(["gateway", "controlUi", "allowInsecureAuth"], v),
          true,
        )}

        ${renderToggle(
          "Disable Device Auth",
          "Skip device pairing verification. DANGEROUS -- any client can connect.",
          disableDeviceAuth,
          (v) => props.onConfigPatch(["gateway", "controlUi", "dangerouslyDisableDeviceAuth"], v),
          true,
        )}
      </div>

      <!-- TLS Configuration -->
      <div style=${SECTION_STYLE}>
        <div style=${SECTION_TITLE_STYLE}>TLS Configuration</div>

        ${renderToggle(
          "TLS Enabled",
          "Enable TLS encryption for gateway connections.",
          tlsEnabled,
          (v) => props.onConfigPatch(["gateway", "tls", "enabled"], v),
        )}

        ${renderToggle(
          "Auto-generate TLS Certificates",
          "Automatically generate self-signed certificates when TLS is enabled.",
          tlsAutoGen,
          (v) => props.onConfigPatch(["gateway", "tls", "autoGenerate"], v),
        )}

        <div style="padding:8px 0;">
          <div style=${LABEL_STYLE}>Remote TLS Fingerprint</div>
          <div style=${HELP_STYLE}>SHA-256 fingerprint (64 hex chars) for pinned TLS verification.</div>
          <input
            type="text"
            .value=${tlsFingerprint}
            placeholder="e.g. a1b2c3d4e5f6... (64 hex characters)"
            @input=${(e: Event) =>
              props.onConfigPatch(
                ["gateway", "remote", "tlsFingerprint"],
                (e.target as HTMLInputElement).value || undefined,
              )}
            style="width:100%;margin-top:6px;padding:8px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);font-family:monospace;font-size:0.85em;"
          />
        </div>
      </div>

      <!-- CORS & Network -->
      <div style=${SECTION_STYLE}>
        <div style=${SECTION_TITLE_STYLE}>CORS & Network</div>

        <div style="margin-bottom:16px;">
          <div style=${LABEL_STYLE}>Allowed Origins</div>
          <div style=${HELP_STYLE}>CORS origins permitted to access the Control UI.</div>
          ${renderTagList(
            allowedOrigins,
            (item) => {
              const next = allowedOrigins.filter((o) => o !== item);
              props.onConfigPatch(["gateway", "controlUi", "allowedOrigins"], next);
            },
            (item) => {
              props.onConfigPatch(
                ["gateway", "controlUi", "allowedOrigins"],
                [...allowedOrigins, item],
              );
            },
            "https://example.com",
          )}
        </div>

        <div style="margin-bottom:16px;">
          <div style=${LABEL_STYLE}>Trusted Proxies</div>
          <div style=${HELP_STYLE}>IP addresses or CIDR ranges of trusted reverse proxies.</div>
          ${renderTagList(
            trustedProxies,
            (item) => {
              const next = trustedProxies.filter((p) => p !== item);
              props.onConfigPatch(["gateway", "trustedProxies"], next);
            },
            (item) => {
              props.onConfigPatch(["gateway", "trustedProxies"], [...trustedProxies, item]);
            },
            "192.168.1.0/24",
          )}
        </div>
      </div>

      <!-- Content Security -->
      <div style=${SECTION_STYLE}>
        <div style=${SECTION_TITLE_STYLE}>Content Security</div>

        ${renderToggle(
          "Allow Unsafe External Content (Gmail)",
          "Disables prompt injection defense for Gmail hook content. DANGEROUS -- external emails can inject instructions.",
          gmailUnsafe,
          (v) => props.onConfigPatch(["hooks", "gmail", "allowUnsafeExternalContent"], v),
          true,
        )}

        <div style="padding:8px 0;">
          <div style=${LABEL_STYLE}>Prompt Injection Detection</div>
          <div style=${HELP_STYLE}>
            ${SUSPICIOUS_PATTERN_COUNT} regex patterns actively scan external content for injection attempts.
            Content is wrapped in boundary markers and prefixed with safety instructions when protection is enabled.
          </div>
        </div>
      </div>

      <!-- Node & Command Control -->
      <div style=${SECTION_STYLE}>
        <div style=${SECTION_TITLE_STYLE}>Node & Command Control</div>

        <div style="margin-bottom:16px;">
          <div style=${LABEL_STYLE}>Allowed Commands</div>
          <div style=${HELP_STYLE}>Commands that remote nodes are permitted to execute. Empty = default set.</div>
          ${renderTagList(
            allowCommands,
            (item) => {
              const next = allowCommands.filter((c) => c !== item);
              props.onConfigPatch(["gateway", "nodes", "allowCommands"], next);
            },
            (item) => {
              props.onConfigPatch(["gateway", "nodes", "allowCommands"], [...allowCommands, item]);
            },
            "command-name",
          )}
        </div>

        <div style="margin-bottom:16px;">
          <div style=${LABEL_STYLE}>Denied Commands</div>
          <div style=${HELP_STYLE}>Commands explicitly blocked from remote node execution.</div>
          ${renderTagList(
            denyCommands,
            (item) => {
              const next = denyCommands.filter((c) => c !== item);
              props.onConfigPatch(["gateway", "nodes", "denyCommands"], next);
            },
            (item) => {
              props.onConfigPatch(["gateway", "nodes", "denyCommands"], [...denyCommands, item]);
            },
            "command-name",
          )}
        </div>
      </div>

      <!-- Skill Download Security (read-only) -->
      <div style=${SECTION_STYLE}>
        <div style=${SECTION_TITLE_STYLE}>Skill Download Security</div>

        ${renderInfoBox("Allowed Download Domains", ALLOWED_DOWNLOAD_DOMAINS)}

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
          <div style=${INFO_BOX_STYLE}>
            <div style="font-weight:500;margin-bottom:4px;">Path Traversal Guard</div>
            <div style="color:var(--accent,#4a9);font-weight:600;">ACTIVE</div>
            <div style="font-size:0.8em;color:var(--fg-muted,#888);margin-top:2px;">
              Downloads confined to ~/.i-gloobot/tools/
            </div>
          </div>
          <div style=${INFO_BOX_STYLE}>
            <div style="font-weight:500;margin-bottom:4px;">Code Scanner</div>
            <div style="color:var(--accent,#4a9);font-weight:600;">ACTIVE</div>
            <div style="font-size:0.8em;color:var(--fg-muted,#888);margin-top:2px;">
              Max ${SKILL_SCANNER_MAX_FILES} files, ${(SKILL_SCANNER_MAX_FILE_BYTES / 1024 / 1024).toFixed(0)} MB per file
            </div>
          </div>
        </div>
      </div>

      <!-- Browser Security (read-only) -->
      <div style=${SECTION_STYLE}>
        <div style=${SECTION_TITLE_STYLE}>Browser Security</div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
          <div style=${INFO_BOX_STYLE}>
            <div style="font-weight:500;margin-bottom:4px;">Custom Module Override</div>
            <div style="color:var(--accent,#4a9);font-weight:600;">BLOCKED</div>
            <div style="font-size:0.8em;color:var(--fg-muted,#888);margin-top:2px;">
              IGLOOBOT_BROWSER_CONTROL_MODULE ignored
            </div>
          </div>
          <div style=${INFO_BOX_STYLE}>
            <div style="font-weight:500;margin-bottom:4px;">Eval Size Limit</div>
            <div style="font-weight:600;">${(MAX_EVAL_FN_LENGTH / 1000).toFixed(0)} KB</div>
            <div style="font-size:0.8em;color:var(--fg-muted,#888);margin-top:2px;">
              Max function body for browser evaluate
            </div>
          </div>
        </div>
      </div>

      <!-- SSRF Protection (read-only) -->
      <div style=${SECTION_STYLE}>
        <div style=${SECTION_TITLE_STYLE}>SSRF Protection</div>

        ${renderInfoBox("Blocked Hostnames & Patterns", SSRF_BLOCKED)}

        <div style=${HELP_STYLE}>
          Private network ranges (10.x, 172.16-31.x, 192.168.x, fc00::/7, ::1) are also blocked by default.
          Max redirects: 3.
        </div>
      </div>

      <!-- Runtime Override Protection (read-only) -->
      <div style=${SECTION_STYLE}>
        <div style=${SECTION_TITLE_STYLE}>Runtime Override Protection</div>

        ${renderInfoBox("Blocked Config Override Paths", BLOCKED_OVERRIDE_PREFIXES)}

        <div style=${HELP_STYLE}>
          These config paths cannot be modified via runtime overrides (setConfigOverride).
          Changes require editing the config file directly and restarting the gateway.
        </div>
      </div>

      <!-- Save / Reload bar -->
      ${
        cfg
          ? html`
            <div style="display:flex;gap:8px;justify-content:flex-end;padding:12px 0;">
              <button
                class="btn"
                @click=${props.onConfigReload}
                ?disabled=${props.configLoading}
              >
                ${props.configLoading ? "Loading..." : "Reload"}
              </button>
              <button
                class="btn btn--primary"
                @click=${props.onConfigSave}
                ?disabled=${props.configSaving || !props.configDirty}
              >
                ${props.configSaving ? "Saving..." : props.configDirty ? "Save Changes" : "Saved"}
              </button>
            </div>
          `
          : html`
            <div class="callout" style="margin-top:12px;">
              ${
                props.connected
                  ? "Loading configuration..."
                  : "Connect to the gateway to manage security settings."
              }
            </div>
          `
      }
    </div>
  `;
}
